//
// ============================================================================
// IMPORTS
// ============================================================================
//

// Types
import type {
  FilterDoctor,
  FilterInstrument,
  FilterLocation,
  FilterOptions,
  FilterRequester,
  FilterSampleType,
  FilterVisitType,
} from "../../services/api/domains/filters/filters.types";

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Filters Composable
 *
 * Manages filter options for sample/visit filtering throughout the application.
 * Provides centralized access to locations, instruments, sample types, visit types,
 * requesters, and doctors for consistent filtering UI.
 *
 * Responsibilities:
 * - Fetch and cache all filter option categories
 * - Provide individual fetch methods for each filter type
 * - Extract instruments from locations (nested structure)
 * - Format doctor names for display
 * - Convert sample type colors to CSS format
 * - Support batch fetch (fetchAllFilters) for efficiency
 *
 * @param key - Unique key for state isolation (default: "filters")
 */
export const useFilters = (key = "filters") => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  const { withAbort, abortAll } = useAbortableMultiFetch();
  const { handleError } = useErrorBoundary();

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //
  const loading = useState<boolean>(`${key}-loading`, () => false);
  const error = useState<string | null>(`${key}-error`, () => null);
  const locations = useState<FilterLocation[]>(`${key}-locations`, () => []);
  const sampleTypes = useState<FilterSampleType[]>(`${key}-sample-types`, () => []);
  const visitTypes = useState<FilterVisitType[]>(`${key}-visit-types`, () => []);
  const requesters = useState<FilterRequester[]>(`${key}-requesters`, () => []);
  const doctors = useState<FilterDoctor[]>(`${key}-doctors`, () => []);

  //
  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================
  //

  /**
   * Extract all instruments from nested location structure
   *
   * Purpose:
   * Flatten instruments from all locations into a single array.
   * Locations contain nested instruments; this provides direct instrument access.
   *
   * Responsibilities:
   * - Iterate through all locations
   * - Extract instruments array from each location
   * - Flatten into single array using flatMap
   *
   * @returns Flat array of all instruments from all locations
   */
  const allInstruments = computed<FilterInstrument[]>(() => {
    return locations.value.flatMap((location) => location.instruments);
  });

  /**
   * Format doctor names for display
   *
   * Purpose:
   * Generate full names from doctor name components for user-friendly display.
   * Format: "FamilyName, GivenName MiddleName"
   *
   * Responsibilities:
   * - Combine familyName, givenName, and middleName
   * - Format as "LastName, FirstName MiddleName"
   * - Trim whitespace for names without middle name
   * - Preserve all original doctor properties
   *
   * @returns Array of doctors with added fullName property
   */
  const doctorsWithFullName = computed(() => {
    return doctors.value.map((doctor) => ({
      ...doctor,
      fullName: `${doctor.familyName}, ${doctor.givenName} ${doctor.middleName}`.trim(),
    }));
  });

  /**
   * Aggregate all filter options into single object
   *
   * Purpose:
   * Provide convenient access to all filter types in one object.
   * Simplifies passing filters to components and composables.
   *
   * Responsibilities:
   * - Combine all filter category arrays
   * - Include flattened instruments (not nested in locations)
   * - Return typed FilterOptions object
   *
   * @returns Complete filter options object with all categories
   */
  const filterOptions = computed<FilterOptions>(() => ({
    locations: locations.value,
    instruments: allInstruments.value,
    sampleTypes: sampleTypes.value,
    visitTypes: visitTypes.value,
    requesters: requesters.value,
    doctors: doctors.value,
  }));

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Fetch all filter categories in parallel (batch operation)
   *
   * Purpose:
   * Efficiently retrieve all filter options in a single operation.
   * Optimizes initial page load by fetching all filters simultaneously.
   *
   * Responsibilities:
   * - Fetch all 5 filter categories in parallel using Promise.all
   * - Update respective state for each category
   * - Handle partial failures gracefully (some may succeed)
   * - Return aggregated filter options
   * - Handle request abortion
   *
   * @returns Complete filter options object or null if aborted
   */
  const fetchAllFilters = async (): Promise<FilterOptions | null> => {
    loading.value = true;
    error.value = null;

    try {
      const [locationsData, sampleTypesData, visitTypesData, requestersData, doctorsData] = await Promise.all([
        withAbort(async (signal) => {
          const cl = await useProdClient();
          return cl.filters.fetchLocations(signal);
        }),
        withAbort(async (signal) => {
          const cl = await useProdClient();
          return cl.filters.fetchSampleTypes(signal);
        }),
        withAbort(async (signal) => {
          const cl = await useProdClient();
          return cl.filters.fetchVisitTypes(signal);
        }),
        withAbort(async (signal) => {
          const cl = await useProdClient();
          return cl.filters.fetchRequesters(signal);
        }),
        withAbort(async (signal) => {
          const cl = await useProdClient();
          return cl.filters.fetchDoctors(signal);
        }),
      ]);

      if (locationsData) locations.value = locationsData;
      if (sampleTypesData) sampleTypes.value = sampleTypesData;
      if (visitTypesData) visitTypes.value = visitTypesData;
      if (requestersData) requesters.value = requestersData;
      if (doctorsData) doctors.value = doctorsData;

      return filterOptions.value;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchAllFilters",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message || "Failed to fetch filters";
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetch locations with nested instruments
   *
   * Purpose:
   * Retrieve laboratory locations and their associated instruments.
   * Locations contain nested instrument arrays for hierarchical filtering.
   *
   * Responsibilities:
   * - Fetch locations from API
   * - Each location includes instruments array
   * - Update locations state
   * - Handle request abortion
   *
   * @returns Array of locations with nested instruments or null if aborted/error
   */
  const fetchLocations = async (): Promise<FilterLocation[] | null> => {
    loading.value = true;
    error.value = null;

    try {
      const response = await withAbort(async (signal) => {
        const cl = await useProdClient();
        return cl.filters.fetchLocations(signal);
      });

      if (response) locations.value = response;
      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchLocations",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message || "Failed to fetch locations";
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetch sample type categories
   *
   * Purpose:
   * Retrieve available sample types (blood, urine, etc.) with color coding.
   * Sample types include RGB color values for visual differentiation.
   *
   * Responsibilities:
   * - Fetch sample types from API
   * - Each type includes color object (red, green, blue, alpha)
   * - Update sampleTypes state
   * - Handle request abortion
   *
   * @returns Array of sample types with colors or null if aborted/error
   */
  const fetchSampleTypes = async (): Promise<FilterSampleType[] | null> => {
    loading.value = true;
    error.value = null;

    try {
      const response = await withAbort(async (signal) => {
        const cl = await useProdClient();
        return cl.filters.fetchSampleTypes(signal);
      });

      if (response) sampleTypes.value = response;
      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchSampleTypes",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message || "Failed to fetch sample types";
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetch visit type categories
   *
   * Purpose:
   * Retrieve types of patient visits (inpatient, outpatient, emergency, etc.).
   *
   * Responsibilities:
   * - Fetch visit types from API
   * - Update visitTypes state
   * - Handle request abortion
   *
   * @returns Array of visit types or null if aborted/error
   */
  const fetchVisitTypes = async (): Promise<FilterVisitType[] | null> => {
    loading.value = true;
    error.value = null;

    try {
      const response = await withAbort(async (signal) => {
        const cl = await useProdClient();
        return cl.filters.fetchVisitTypes(signal);
      });

      if (response) visitTypes.value = response;
      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchVisitTypes",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message || "Failed to fetch visit types";
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetch requesting physicians/departments
   *
   * Purpose:
   * Retrieve list of physicians or departments that can order tests.
   *
   * Responsibilities:
   * - Fetch requesters from API
   * - Update requesters state
   * - Handle request abortion
   *
   * @returns Array of requesters or null if aborted/error
   */
  const fetchRequesters = async (): Promise<FilterRequester[] | null> => {
    loading.value = true;
    error.value = null;

    try {
      const response = await withAbort(async (signal) => {
        const cl = await useProdClient();
        return cl.filters.fetchRequesters(signal);
      });

      if (response) requesters.value = response;
      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchRequesters",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message || "Failed to fetch requesters";
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetch doctor list with name components
   *
   * Purpose:
   * Retrieve physicians with separated name components.
   * Use doctorsWithFullName computed for formatted display.
   *
   * Responsibilities:
   * - Fetch doctors from API
   * - Each doctor has familyName, givenName, middleName
   * - Update doctors state
   * - Handle request abortion
   *
   * @returns Array of doctors with name components or null if aborted/error
   */
  const fetchDoctors = async (): Promise<FilterDoctor[] | null> => {
    loading.value = true;
    error.value = null;

    try {
      const response = await withAbort(async (signal) => {
        const cl = await useProdClient();
        return cl.filters.fetchDoctors(signal);
      });

      if (response) doctors.value = response;
      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchDoctors",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message || "Failed to fetch doctors";
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Convert sample type color to CSS rgba() format
   *
   * Purpose:
   * Transform API color object to CSS-compatible rgba string.
   * Enables direct use in style bindings and CSS.
   *
   * Responsibilities:
   * - Extract RGBA values from sampleType.color
   * - Convert alpha from 0-255 range to 0-1 range
   * - Format as CSS rgba() string
   *
   * @param sampleType - Sample type with color object
   * @returns CSS rgba() color string
   */
  const getSampleTypeColorString = (sampleType: FilterSampleType): string => {
    const { red, green, blue, alpha } = sampleType.color;
    return `rgba(${red}, ${green}, ${blue}, ${alpha / 255})`;
  };

  return {
    // State
    loading: readonly(loading),
    error: readonly(error),
    locations: readonly(locations),
    sampleTypes: readonly(sampleTypes),
    visitTypes: readonly(visitTypes),
    requesters: readonly(requesters),
    doctors: readonly(doctors),

    // Computed
    allInstruments: readonly(allInstruments),
    doctorsWithFullName: readonly(doctorsWithFullName),
    filterOptions: readonly(filterOptions),

    // Methods
    fetchAllFilters,
    fetchLocations,
    fetchSampleTypes,
    fetchVisitTypes,
    fetchRequesters,
    fetchDoctors,
    getSampleTypeColorString,
    abortAll,
  };
};
